// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * View deep-link handlers.
 *
 * Query parameters that open the app in a pre-set state: `?layout=4`
 * drops into a 4-globe layout, `?terrain=on` enables 3D terrain,
 * `?orbit=open` surfaces the chat panel, etc. (TerraViz's companion
 * poster used them first.)
 *
 * This module reads the query string at app boot and dispatches
 * the corresponding actions through the same APIs that the in-app
 * Tools menu and chat trigger use, so analytics emits, accessibility
 * announcements, and button-state UI all stay in sync. Behaviour is
 * conservative: an unknown param value is a silent no-op rather than
 * an error; the URL is not rewritten or cleared after dispatch so a
 * page refresh preserves the deep-linked state.
 *
 * Layout (`?layout=`) is read separately by main.ts during renderer
 * init — see `parseInitialLayout` below — so the panel grid is
 * already correct by the time the view toggles fire.
 *
 * AYNI (2026-10-07): the poster's guided-sequence parameter, and the
 * chat seed that asked which one to start with, went with TerraViz's
 * player; a story is a dataset link like any other (`?dataset=`).
 */

import { logger } from './logger'
import type { ViewLayout } from '../services/viewportManager'

/**
 * Bridge from this module to the App. Each method dispatches through
 * the canonical app API; we never poke renderers or DOM directly
 * (except for the Tools-menu buttons, which is the right call site
 * for analytics + a11y + button-state side effects).
 */
export interface PosterDeepLinkContext {
  /** Open the chat panel, optionally pre-filling the input. */
  openChatWithQuery: (query?: string) => void
}

/**
 * Map the public `?layout=` (and legacy `?setview=`) param to a
 * canonical {@link ViewLayout}, or null if the value is unknown.
 *
 * Public layout values the poster ships: `1` / `2` / `4`. The legacy
 * `?setview=` form additionally accepts `2h` and `2v` as advanced
 * orientations; we honour those too.
 */
export function resolveLayout(raw: string | null, portrait = false): ViewLayout | null {
  if (!raw) return null
  if (raw === '1' || raw === '2h' || raw === '2v' || raw === '4') return raw
  // AYNI (multi-globe plan): the public "2" takes the orientation of the
  // screen it opens on, as the budget clamp does: stacked on a portrait
  // phone, where side by side gives each globe under 200 px and its head
  // row no room; side by side elsewhere. An explicit `2h` or `2v` is kept.
  if (raw === '2') return portrait ? '2v' : '2h'
  return null
}

/**
 * Read the initial viewport layout from the URL. Prefers the public
 * `?layout=` param; falls back to the legacy dev `?setview=` param.
 * Returns the canonical {@link ViewLayout} or `'1'` (single globe)
 * if neither param is present or recognised.
 */
export function parseInitialLayout(search: string, portrait = typeof window !== 'undefined' && window.innerHeight >= window.innerWidth): ViewLayout {
  const params = new URLSearchParams(search)
  const layout = resolveLayout(params.get('layout'), portrait)
  if (layout) return layout
  const legacy = resolveLayout(params.get('setview'), portrait)
  if (legacy) return legacy
  return '1'
}

/**
 * Apply view deep-link query parameters to the live app. Called
 * once from main.ts after the catalog has loaded and the Tools menu
 * has wired its buttons.
 *
 * Order:
 *   1. View toggles (`?terrain=`, `?labels=`, `?borders=`,
 *      `?rotate=`) — clicked through the Tools-menu buttons so the
 *      analytics emit + a11y announce + button-state UI all stay in
 *      sync.
 *   2. `?orbit=open` — open the chat panel. Runs last so nothing
 *      dismisses the panel after we open it.
 */
export async function applyPosterDeepLinks(
  ctx: PosterDeepLinkContext,
): Promise<void> {
  const params = new URLSearchParams(window.location.search)

  // 1. View toggles — drive through the Tools-menu buttons.
  clickToolsMenuIfOff('terrain', 'tools-menu-terrain', params)
  clickToolsMenuIfOff('labels', 'tools-menu-labels', params)
  clickToolsMenuIfOff('borders', 'tools-menu-borders', params)
  // AYNI: auto-rotate is the button under Help now (autoRotateControl).
  clickToolsMenuIfOff('rotate', 'autorotate-btn', params)

  // 2. The chat panel.
  if (params.get('orbit') === 'open') {
    logger.debug('[PosterDeepLinks] openChat')
    ctx.openChatWithQuery(undefined)
  }
}

/**
 * Click a Tools-menu toggle button if the matching `?<name>=on` query
 * param is set and the toggle is currently off. Routing through the
 * button click means the button's existing handler — which already
 * mirrors state to all renderers, persists prefs, emits analytics,
 * and announces to screen readers — runs unchanged.
 */
function clickToolsMenuIfOff(
  paramName: string,
  buttonId: string,
  params: URLSearchParams,
): void {
  if (params.get(paramName) !== 'on') return
  const btn = document.getElementById(buttonId)
  if (btn instanceof HTMLButtonElement) {
    if (!btn.classList.contains('active')) {
      btn.click()
    }
  } else {
    // Conservative on unknowns: silent debug-level no-op.
    logger.debug(`[PosterDeepLinks] toggle button missing: ${buttonId}`)
  }
}
